import React, { forwardRef, memo, ReactElement, useEffect, useMemo, useState } from 'react'

import { clsx } from 'clsx'
import { useAtomValue } from 'jotai'

import { BffInterfacesDeparture } from '@dfds-pax/booking-experience-bff'

import { JourneyType } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure)/JourneyType'
import { RouteHeader } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure)/RouteHeader'
import { Filters } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure-filters)/Filters'
import { PriceFilter } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure-filters)/PriceSwitch'
import { RouteFilter } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure-filters)/RouteFilters'
import { TimeFilter } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure-filters)/TimeFilters'
import { Collapse } from '@/components/shared/Collapse'
import { selectedDeparturesAtom } from '@/states/selectionAtom'

import { useTranslation } from '@/hooks/useDebugTranslations'

import { DepartureFunctions } from '../../YourBookingHandlers'
import DepartureRowSingleTier from '../departure-row/DepartureRowSingleTier'
import { applyPriceFilter, departureIsWithinTimeFilter, showDeparture } from './DepartureLeg'
import { DepartureNoSailing } from './DeparturesNoSailing'
import ViewHideDeparturesLink from './ViewHideDeparturesLink'

interface Props {
  dateChanged: (date: string, legNumber: 1 | 2) => void
  departureClicked: DepartureFunctions['DepartureClicked']
  departureDate: string
  departures?: BffInterfacesDeparture.Departure[]
  editElement?: ReactElement
  legType: string
  loading: boolean
  onOutboundAccordionToggle: (isOpen: boolean) => void
  onReturnAccordionToggle: (isOpen: boolean) => void
  outboundAccordionOpen: boolean
  returnAccordionOpen: boolean
  returnLeg?: boolean
  selected?: boolean
  selectedDepartureId?: string
  selectedRouteFilters: RouteFilter
  selectedType?: string
  setSelectedRouteFilters: (filters: RouteFilter) => void
  switchElement?: ReactElement
}

const mapper = (d: BffInterfacesDeparture.Departure) => {
  const departureTimeFormatted = d.departureTime?.match(/..:../)?.[0]
  const arrivalTimeFormatted = d.arrivalTime?.match(/..:../)?.[0]
  const checkInTimeFormatted = d.checkinTime?.match(/..:../)?.[0]
  return { ...d, arrivalTimeFormatted, checkInTimeFormatted, departureTimeFormatted }
}

const DepartureLegSingleTier = forwardRef<HTMLDivElement, Props>(
  (
    {
      dateChanged,
      departureClicked,
      departureDate,
      departures,
      editElement,
      legType,
      loading,
      onOutboundAccordionToggle,
      onReturnAccordionToggle,
      outboundAccordionOpen,
      returnAccordionOpen,
      returnLeg,
      selectedDepartureId,
      selectedRouteFilters,
      selectedType,
      setSelectedRouteFilters,
      switchElement,
    }: Props,
    ref
  ): React.ReactElement => {
    const [selectedPriceFilter, _] = useState<PriceFilter>('TIME')
    const [selectedTimeFilters, setSelectedTimeFilters] = useState<TimeFilter>({
      afternoon: false,
      all: true,
      evening: false,
      morning: false,
      night: false,
    })
    const departuresMapped = useMemo(() => departures?.map(mapper), [departures])
    const selectedDepartures = useAtomValue(selectedDeparturesAtom)
    const [departureIdRowOpen, setDepartureIdRowOpen] = useState<string>()
    const [showDepartures, setShowDepartures] = useState<boolean>(false)
    const [filtering, setFiltering] = useState<boolean>(false)

    const { t } = useTranslation()

    // For animating blinking when filtering
    useEffect(() => {
      setTimeout(() => {
        setFiltering(false)
      }, 200)
    }, [selectedTimeFilters])

    let indexForViewHideDeparturesLink: any

    if (returnLeg) {
      const arrivalDateTime = selectedDepartures?.departureOut?.arrivalDateTime
      indexForViewHideDeparturesLink = arrivalDateTime && departuresMapped?.findIndex((x) => x.checkinDateTime > arrivalDateTime)
    } else {
      const checkinDateTime = selectedDepartures?.departureHome?.checkinDateTime
      indexForViewHideDeparturesLink = checkinDateTime && departuresMapped?.findIndex((x) => x.arrivalDateTime > checkinDateTime)
    }

    const filterDepartures = (departure: any) => {
      const departureVisible = departureIsWithinTimeFilter(departure.departureTime, selectedTimeFilters) || false
      if (departureVisible) return departure
    }
    const hasDeparturesShown = departuresMapped?.some((d) => filterDepartures(d))

    return (
      <div
        className="bg-semantic-global-background-light-grey"
        data-e2e-leg={legType.toLowerCase()}
        data-e2e-leg-loading={loading}
        id={legType === 'Outbound' ? `${legType}Leg` : ''}
        ref={ref}
      >
        <div className="mb-semantic-global-sm">
          <RouteHeader editElement={editElement} isOpen={returnLeg ? returnAccordionOpen : outboundAccordionOpen} returnLeg={returnLeg} />
          <Collapse
            isOpen={Boolean(legType && (returnLeg ? returnAccordionOpen && selectedDepartures.departureOut : outboundAccordionOpen))}
          >
            <Filters
              dateChanged={dateChanged}
              departureDate={departureDate}
              isJersey={true}
              onRouteFilterChange={(srf) => setSelectedRouteFilters(srf)}
              onTimeFilterChange={(dtf) => {
                setFiltering(true)
                setSelectedTimeFilters(dtf)
              }}
              returnLeg={returnLeg}
              selectedRouteFilters={selectedRouteFilters}
              selectedTimeFilters={selectedTimeFilters}
            />
          </Collapse>
        </div>

        <ul className="flex flex-col" data-e2e="departures">
          {departuresMapped && departuresMapped.length > 0
            ? applyPriceFilter(
                departuresMapped
                  .filter((d) => filterDepartures(d))
                  .filter(() => {
                    if (returnLeg && !selectedDepartures.departureOut) return false
                    return true
                  }),
                selectedPriceFilter
              ).map((d, i) => {
                const isAccordionOpen = returnLeg ? returnAccordionOpen : outboundAccordionOpen
                const isSelected = selectedDepartureId === d.departureId
                const departureVisible = showDeparture(returnLeg, showDepartures, selectedDepartures, d)
                const showRow = isAccordionOpen
                  ? departureVisible || indexForViewHideDeparturesLink === -1 || indexForViewHideDeparturesLink === 0
                  : isSelected

                return (
                  <div
                    className={clsx('transition-opacity duration-500 ease-in-out', filtering && 'opacity-30 md:opacity-50')}
                    key={`${d.departureTime}-${d.departurePortCode}-${d.arrivalPortCode}`}
                  >
                    <Collapse isOpen={showRow}>
                      <DepartureRowSingleTier
                        accordionOpen={isAccordionOpen}
                        active={departureIsWithinTimeFilter(d.departureTime, selectedTimeFilters) ?? false}
                        arrivalDateTime={d.arrivalDateTime}
                        arrivalPortCode={d.arrivalPortCode}
                        arrivalPortName={t(d.arrivalPortCode)}
                        arrivalTime={d.arrivalTime}
                        checkinDateTime={d.checkinDateTime}
                        checkinTime={d.checkinTime}
                        departureClicked={departureClicked}
                        departureId={d.departureId}
                        departurePortCode={d.departurePortCode}
                        departurePortName={t(d.departurePortCode)}
                        departureTime={d.departureTime}
                        departureTimeFormatted={d.departureTime?.match(/..:../)?.[0]}
                        departureVisible={departureIsWithinTimeFilter(d.departureTime, selectedTimeFilters)}
                        isOpen={departureIdRowOpen === d.departureId}
                        loading={loading}
                        onDepartureRowClicked={(departureId: string) => setDepartureIdRowOpen(departureId)}
                        onEditClicked={() => {
                          returnLeg ? onReturnAccordionToggle(true) : onOutboundAccordionToggle(true)
                        }}
                        returnLeg={returnLeg}
                        selected={isSelected}
                        selectedType={selectedType}
                        tickets={d.tickets}
                        tripDuration={d.tripDuration}
                      />
                    </Collapse>
                    {indexForViewHideDeparturesLink === i + 1 && isAccordionOpen ? (
                      <ViewHideDeparturesLink returnLeg={returnLeg} setShowDepartures={setShowDepartures} showDepartures={showDepartures} />
                    ) : null}
                  </div>
                )
              })
            : null}
          {!hasDeparturesShown && departures ? <DepartureNoSailing /> : null}
        </ul>
        <JourneyType switchElement={switchElement} />
      </div>
    )
  }
)

DepartureLegSingleTier.displayName = 'LegSingleTier'

export default memo(DepartureLegSingleTier)
